import './SummaryView.scss';
import { useMemo, useState } from 'react';
import cn from 'classnames';
import * as R from 'ramda';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PlanRow } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import {
  formatMoney,
  formatMoneyAsInt,
  formatNumberAsInt
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { BPMTable } from '@Components/StickyTable';
import { Spinner } from '@Components/Spinner';
import { ChartContainer } from '@Components/ChartContainer';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Size } from '@Components/designConstants';
import { CreativeMap } from '@src/redux/creative';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  EditsMap,
  makeSummaryData,
  mergeAllRows,
  NielsenEstimates,
  SpendObj
} from './linear-buying-utils';

interface SummaryViewProps {
  rows: PlanRow[];
  editsMap: EditsMap;
  newRows: Record<string, PlanRow>;
  creativeMap: CreativeMap | undefined;
  weeks: string[];
  nielsenEstimates: NielsenEstimates;
  /** Every number on this view is derived from the Nielsen estimates, so it waits for them. */
  isLoading: boolean;
}

// The breakdowns with an unbounded row count — a buy can carry dozens of networks or
// creatives — so they share one full-width table rather than competing for height.
const SpendDimension = {
  NETWORK: 'network',
  CREATIVE: 'creative',
  ROTATION: 'rotation'
} as const;
type SpendDimensionType = (typeof SpendDimension)[keyof typeof SpendDimension];

const DIMENSION_OPTIONS = [
  { key: SpendDimension.NETWORK, label: 'Network' },
  { key: SpendDimension.CREATIVE, label: 'Creative' },
  { key: SpendDimension.ROTATION, label: 'Rotation' }
];

// Number of segment colors defined as `.length-*` classes. Lengths cycle through them.
const LENGTH_COLORS = 4;

const asPercent = (value: number): string =>
  Number.isFinite(value) ? `${Math.round(value * 100)}%` : '-';

interface KpiCardProps {
  label: string;
  value: string;
}

const KpiCard = ({ label, value }: KpiCardProps) => (
  <div className='kpi-card'>
    <div className='kpi-label'>{label}</div>
    <div className='kpi-value'>{value}</div>
  </div>
);

interface SharePercentCellProps {
  percent: number;
}

const SharePercentCell = ({ percent }: SharePercentCellProps) => {
  const fraction = Number.isFinite(percent) ? Math.min(Math.max(percent, 0), 1) : 0;

  return (
    <div className='share-cell'>
      <div className='share-track'>
        <div className='share-fill' style={{ width: `${fraction * 100}%` }} />
      </div>
      <span className='share-value'>{asPercent(percent)}</span>
    </div>
  );
};

const makeTableHeaders = (primaryColumnLabel: string, primaryColumnName: string) => [
  {
    label: primaryColumnLabel,
    name: primaryColumnName,
    flex: 1,
    minFlexWidth: 300
  },
  {
    label: 'Spend',
    name: 'spend',
    flex: 1,
    renderer: (row: SpendObj) => formatMoneyAsInt(row.spend)
  },
  {
    label: 'Percent',
    name: 'percent',
    flex: 1,
    minFlexWidth: 120,
    renderer: (row: SpendObj) => <SharePercentCell percent={row.percent} />
  },
  {
    label: 'Impressions',
    name: 'impressions',
    flex: 1,
    renderer: (row: SpendObj) => formatNumberAsInt(row.impressions)
  },
  {
    label: 'CPM',
    name: 'cpm',
    flex: 1,
    renderer: (row: SpendObj) => formatMoney(row.cpm, 2)
  }
];

interface MixSegment {
  key: string;
  // Share of the whole track, so a row of segments sums to that row's share of total spend.
  widthPercent: number;
  colorIndex: number;
  label?: string;
  tooltip: string;
}

interface MixBarProps {
  segments: MixSegment[];
}

const MixBar = ({ segments }: MixBarProps) => (
  <div className='mix-track'>
    {segments.map(({ key, widthPercent, colorIndex, label, tooltip }) => (
      <OverlayTrigger key={key} placement='top' overlay={<Tooltip id={key}>{tooltip}</Tooltip>}>
        <div
          className={cn('mix-segment', `length-${colorIndex % LENGTH_COLORS}`)}
          style={{ width: `${widthPercent}%` }}
        >
          {label && <span className='mix-segment-label'>{label}</span>}
        </div>
      </OverlayTrigger>
    ))}
  </div>
);

interface MixLegendProps {
  lengths: string[];
}

const MixLegend = ({ lengths }: MixLegendProps) => (
  <div className='mix-legend'>
    {lengths.map((length, index) => (
      <div className='legend-item' key={length}>
        <span className={cn('legend-swatch', `length-${index % LENGTH_COLORS}`)} />
        {`${length}s`}
      </div>
    ))}
  </div>
);

interface AvailMixRow {
  avail: string;
  percentOfSpend: number;
  spend: number;
  segments: MixSegment[];
}

interface SpendMixProps {
  lengths: string[];
  lengthSegments: MixSegment[];
  availRows: AvailMixRow[];
}

const SpendMix = ({ lengths, lengthSegments, availRows }: SpendMixProps) => {
  if (!lengths.length) {
    return <div className='mix-empty'>No spend to summarize.</div>;
  }

  return (
    <div className='spend-mix'>
      <div className='mix-section'>
        <div className='mix-section-label'>Length</div>
        <MixBar segments={lengthSegments} />
      </div>
      <div className='mix-section'>
        <div className='mix-section-label'>By avail</div>
        <div className='avail-rows'>
          {availRows.map(({ avail, percentOfSpend, spend, segments }) => (
            <div className='avail-row' key={avail}>
              <div className='avail-name'>{avail}</div>
              <MixBar segments={segments} />
              <div className='avail-value'>
                <span className='avail-percent'>{asPercent(percentOfSpend)}</span>
                <span className='avail-spend'>{formatMoneyAsInt(spend)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SummaryView = ({
  rows,
  editsMap,
  newRows,
  creativeMap,
  weeks,
  nielsenEstimates,
  isLoading
}: SummaryViewProps) => {
  const [dimension, setDimension] = useState<SpendDimensionType>(SpendDimension.NETWORK);

  const allRows = useMemo(() => {
    let newRowsForCurrentWeek: Record<string, PlanRow> = {};
    let editsForCurrentWeeks: EditsMap = {};
    for (const week of weeks) {
      newRowsForCurrentWeek = {
        ...newRowsForCurrentWeek,
        ...R.pickBy(val => val.week === week, newRows)
      };
      editsForCurrentWeeks = {
        ...editsForCurrentWeeks,
        ...R.pickBy(val => val.week === week, editsMap)
      };
    }

    return mergeAllRows(rows, editsForCurrentWeeks, newRowsForCurrentWeek);
  }, [rows, editsMap, newRows, weeks]);

  const { totalSpend, byLength, byNetwork, byRotation, byCreative, byAvailLength } = useMemo(
    () => makeSummaryData({ rows: allRows, nielsenEstimates, creativeMap }),
    [allRows, nielsenEstimates, creativeMap]
  );

  // `makeSummaryData` already returns these sorted by spend descending, so the table opens on
  // whatever moved the most money.
  const rowsByDimension: Record<SpendDimensionType, SpendObj[]> = useMemo(
    () => ({
      [SpendDimension.NETWORK]: byNetwork,
      [SpendDimension.CREATIVE]: byCreative,
      [SpendDimension.ROTATION]: byRotation
    }),
    [byNetwork, byCreative, byRotation]
  );

  // Every plan row has a network, so the network breakdown covers the whole buy.
  const totalImpressions = useMemo(
    () => byNetwork.reduce((sum, row) => sum + row.impressions, 0),
    [byNetwork]
  );
  const blendedCpm = totalImpressions ? (totalSpend / totalImpressions) * 1000 : 0;
  const prettyTotalSpend = formatMoneyAsInt(totalSpend);

  // `byLength` is `byAvailLength` collapsed across avails — the same spend partitioned once
  // by length and again by avail and length — so both render off one shared set of lengths.
  const { lengths, lengthSegments, availRows } = useMemo(() => {
    const sortedLengths = [...byLength].sort((a, b) => Number(a.length) - Number(b.length));
    const lengthOrder = sortedLengths.map(row => String(row.length));

    return {
      lengths: lengthOrder,
      lengthSegments: sortedLengths.map((row, index) => {
        const length = String(row.length);

        return {
          key: `length-${length}`,
          widthPercent: row.percent * 100,
          colorIndex: index,
          label: `${length}s · ${asPercent(row.percent)}`,
          tooltip: `${length}s · ${formatMoneyAsInt(row.spend)} · ${formatNumberAsInt(
            row.impressions
          )} impressions · ${formatMoney(row.cpm, 2)} CPM`
        };
      }),
      // Unlike the other breakdowns, `byAvailLength` isn't pre-sorted.
      availRows: [...byAvailLength]
        .sort((a, b) => b.totalSpendByAvail - a.totalSpendByAvail)
        .map(row => ({
          avail: row.avail,
          percentOfSpend: row.totalPercentByAvail,
          spend: row.totalSpendByAvail,
          segments: lengthOrder
            .map((length, index) => ({ length, index, percent: row[Number(length)] || 0 }))
            .filter(({ percent }) => percent > 0)
            .map(({ length, index, percent }) => ({
              key: `${row.avail}-${length}`,
              widthPercent: percent * 100,
              colorIndex: index,
              tooltip: `${row.avail} · ${length}s · ${formatMoneyAsInt(
                percent * totalSpend
              )} · ${asPercent(percent)} of spend`
            }))
        }))
    };
  }, [byLength, byAvailLength, totalSpend]);

  const dimensionRows = rowsByDimension[dimension];
  const dimensionLabel =
    DIMENSION_OPTIONS.find(option => option.key === dimension)?.label ?? 'Network';

  // Unlike the edit table, there's no useful partial view here — spend, impressions and CPM all
  // come out of `makeSummaryData`, so without estimates the whole view would read as zeroes.
  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className='summary-view'>
      <div className='summary-kpis'>
        <KpiCard label='Total Spend' value={prettyTotalSpend} />
        <KpiCard label='Impressions' value={formatNumberAsInt(totalImpressions)} />
        <KpiCard label='Blended CPM' value={formatMoney(blendedCpm, 2)} />
      </div>
      <ChartContainer
        className='summary-panel breakdown-panel'
        title={`By ${dimensionLabel}`}
        subHeader={conditionallyPluralizeWithCount(dimensionRows.length, dimension)}
        viewRightActions
        rightActions={
          <SegmentedControlButton
            options={DIMENSION_OPTIONS}
            selectedOption={dimension}
            onChange={setDimension}
            size={Size.SM}
          />
        }
      >
        <BPMTable<SpendObj>
          data={dimensionRows}
          headers={makeTableHeaders(dimensionLabel, dimension)}
          filterBar={false}
          headerHeight={50}
          rowHeight={40}
        />
      </ChartContainer>
      <ChartContainer
        className='summary-panel mix-panel'
        title='Spend Mix'
        subHeader={conditionallyPluralizeWithCount(availRows.length, 'avail')}
        viewRightActions
        rightActions={<MixLegend lengths={lengths} />}
      >
        <SpendMix lengths={lengths} lengthSegments={lengthSegments} availRows={availRows} />
      </ChartContainer>
    </div>
  );
};
